import React, { Component } from 'react';
import { Table, Button, Label } from 'react-bootstrap';
import { translate } from 'react-i18next';
import Moment from 'moment';
import './AuditLogsTable.css';

class AuditLogsTable extends Component {
    constructor(props) {
        super(props);
    }

    getActionBadgeStyle(action) {
        // Authentication actions
        if (['login', 'logout', '2fa_verify', 'disable_2fa', 'set_google_auth'].includes(action)) {
            return action === 'logout' || action === 'disable_2fa' ? 'warning' : 'success';
        }

        // Create/Add actions
        if (['create', 'create_text', 'upload_media', 'replace_media', 'add_attribute',
             'add_favorite', 'add_page_players', 'add_user_players', 'add_text_overlay',
             'add_medialibrary', 'add_facttype', 'add_questiontype', 'add_textlibrary',
             'add_attribute_players'].includes(action)) {
            return 'success';
        }

        // Update actions
        if (['update', 'update_enabled', 'update_duration', 'update_uploaded',
             'update_muted', 'update_user_role', 'update_locked_globals',
             'set_input_pin', 'reorder'].includes(action)) {
            return 'info';
        }

        // Delete/Remove actions
        if (['delete', 'delete_invite', 'delete_rule', 'delete_rulegroup',
             'bulk_delete', 'remove_user', 'remove_attribute', 'remove_favorite',
             'remove_user_player', 'remove_text_overlay', 'remove_medialibrary',
             'remove_facttype', 'remove_questiontype', 'remove_textlibrary',
             'remove_page', 'remove'].includes(action)) {
            return 'danger';
        }

        // Read actions
        if (['read', 'find_players'].includes(action)) {
            return 'default';
        }

        // Operations
        if (['apply_page', 'resend_invite', 'player_checkin'].includes(action)) {
            return 'primary';
        }

        // Special cases
        if (action === 'login_failed') return 'warning';

        return 'default';
    }

    getSeverityBadgeStyle(severity) {
        switch (severity) {
            case 'critical':
                return 'danger';
            case 'high':
                return 'danger';
            case 'medium':
                return 'warning';
            case 'low':
                return 'default';
            case 'info':
                return 'info';
            default:
                return 'default';
        }
    }

    getOutcomeIcon(outcome) {
        return outcome === 'success'
            ? <i className="fa fa-check-circle text-success" />
            : <i className="fa fa-times-circle text-danger" />;
    }

    formatTimestamp(timestamp) {
        const m = Moment(timestamp);
        const relative = m.fromNow();
        const absolute = m.format('YYYY-MM-DD HH:mm:ss');
        return { relative, absolute };
    }

    truncateUuid(uuid) {
        if (!uuid) return '-';
        return uuid.length > 12 ? uuid.substring(0, 12) + '...' : uuid;
    }

    getDescription(log) {
        // If API provides a summary, use it!
        if (log.event && log.event.summary) {
            return log.event.summary;
        }

        // Fallback to client-side generation
        const { t } = this.props;
        const entityLabel = t(`AuditLogs.entities.${log.target.type}`) || log.target.type;
        const action = log.event.action;
        const targetName = log.target.name || log.target.displayName || this.truncateUuid(log.target.id);

        // Get parent context for more detail
        const parentName = log.target.parent && (log.target.parent.name || log.target.parent.displayName);
        const parentType = log.target.parent && log.target.parent.type;

        let description = '';

        // Authentication actions
        if (action === 'login' || action === 'logout') {
            description = action === 'login' ? 'Logged in' : 'Logged out';
            if (log.source && log.source.ip) {
                description += ` from ${log.source.ip}`;
            }
            return description;
        }

        if (action === '2fa_verify') {
            return 'Verified 2FA authentication';
        }

        if (action === 'disable_2fa') {
            return 'Disabled 2FA authentication';
        }

        if (action === 'set_google_auth') {
            return 'Set up Google authentication';
        }

        if (action === 'login_failed') {
            description = 'Failed login attempt';
            if (log.source && log.source.ip) {
                description += ` from ${log.source.ip}`;
            }
            return description;
        }

        // Specific create actions
        if (action === 'create_text') {
            return `Created text in ${entityLabel.toLowerCase()} '${targetName}'`;
        }

        if (action === 'upload_media' || action === 'replace_media') {
            const verb = action === 'upload_media' ? 'Uploaded' : 'Replaced';
            description = `${verb} media '${targetName}'`;
            if (parentName && parentType) {
                description += ` to ${parentType.toLowerCase()} '${parentName}'`;
            }
            if (log.details) {
                const fileInfo = [];
                if (log.details.fileSize) {
                    fileInfo.push(this.humanFileSize(log.details.fileSize, true));
                }
                if (log.details.extension) {
                    fileInfo.push(`.${log.details.extension}`);
                }
                if (fileInfo.length > 0) {
                    description += ` (${fileInfo.join(', ')})`;
                }
            }
            return description;
        }

        // Specific add actions
        if (action.startsWith('add_')) {
            const addActions = {
                'add_attribute': `Added attribute '${targetName}'`,
                'add_favorite': `Added '${targetName}' to favorites`,
                'add_page_players': `Added players to page '${targetName}'`,
                'add_user_players': `Added players to user '${targetName}'`,
                'add_text_overlay': `Added text overlay to media '${targetName}'`,
                'add_medialibrary': `Added media library to ${entityLabel.toLowerCase()} '${targetName}'`,
                'add_facttype': `Added fact type to ${entityLabel.toLowerCase()} '${targetName}'`,
                'add_questiontype': `Added question type to ${entityLabel.toLowerCase()} '${targetName}'`,
                'add_textlibrary': `Added text library to ${entityLabel.toLowerCase()} '${targetName}'`,
                'add_attribute_players': `Added players to attribute '${targetName}'`
            };
            return addActions[action] || `Added ${entityLabel.toLowerCase()} '${targetName}'`;
        }

        // Specific update actions
        if (action.startsWith('update_')) {
            const updateActions = {
                'update_enabled': `Changed enabled state for ${entityLabel.toLowerCase()} '${targetName}'`,
                'update_duration': `Updated duration for ${entityLabel.toLowerCase()} '${targetName}'`,
                'update_uploaded': `Updated upload status for ${entityLabel.toLowerCase()} '${targetName}'`,
                'update_muted': `Changed muted state for ${entityLabel.toLowerCase()} '${targetName}'`,
                'update_user_role': `Updated role for user '${targetName}'`,
                'update_locked_globals': `Updated locked globals for ${entityLabel.toLowerCase()} '${targetName}'`
            };
            if (updateActions[action]) {
                return updateActions[action];
            }
        }

        if (action === 'set_input_pin') {
            return `Set input PIN for media library '${targetName}'`;
        }

        if (action === 'reorder') {
            return `Reordered items in ${entityLabel.toLowerCase()} '${targetName}'`;
        }

        // Specific remove actions
        if (action.startsWith('remove_')) {
            const removeActions = {
                'remove_attribute': `Removed attribute from ${entityLabel.toLowerCase()} '${targetName}'`,
                'remove_user': `Removed user '${targetName}'`,
                'remove_favorite': `Removed '${targetName}' from favorites`,
                'remove_user_player': `Removed player from user '${targetName}'`,
                'remove_text_overlay': `Removed text overlay from media '${targetName}'`,
                'remove_medialibrary': `Removed media library from ${entityLabel.toLowerCase()} '${targetName}'`,
                'remove_facttype': `Removed fact type from ${entityLabel.toLowerCase()} '${targetName}'`,
                'remove_questiontype': `Removed question type from ${entityLabel.toLowerCase()} '${targetName}'`,
                'remove_textlibrary': `Removed text library from ${entityLabel.toLowerCase()} '${targetName}'`,
                'remove_page': `Removed page from player '${targetName}'`
            };
            if (removeActions[action]) {
                return removeActions[action];
            }
        }

        // Specific delete actions
        if (action === 'delete_invite') {
            return `Deleted invite for '${targetName}'`;
        }

        if (action === 'delete_rule') {
            return `Deleted rule '${targetName}'`;
        }

        if (action === 'delete_rulegroup') {
            return `Deleted rule group '${targetName}'`;
        }

        if (action === 'bulk_delete') {
            const count = log.details && log.details.count ? log.details.count : 'multiple';
            return `Bulk deleted ${count} ${entityLabel.toLowerCase()}${count === 1 ? '' : 's'}`;
        }

        // Operations
        if (action === 'apply_page') {
            return `Applied page '${targetName}' to player${parentName ? ` '${parentName}'` : ''}`;
        }

        if (action === 'resend_invite') {
            return `Resent invite to '${targetName}'`;
        }

        if (action === 'find_players') {
            return `Searched for players`;
        }

        if (action === 'player_checkin') {
            return `Player '${targetName}' checked in`;
        }

        // Generic create
        if (action === 'create') {
            // More specific "create" descriptions
            if (parentName && parentType) {
                description = `Added ${entityLabel.toLowerCase()} '${targetName}' to ${parentType.toLowerCase()} '${parentName}'`;
            } else {
                description = `Created ${entityLabel.toLowerCase()} '${targetName}'`;
            }

            // Add file details for media uploads
            if (log.target.type === 'media' && log.details) {
                const fileInfo = [];
                if (log.details.fileSize) {
                    fileInfo.push(this.humanFileSize(log.details.fileSize, true));
                }
                if (log.details.extension) {
                    fileInfo.push(`.${log.details.extension}`);
                }
                if (fileInfo.length > 0) {
                    description += ` (${fileInfo.join(', ')})`;
                }
            }
        } else if (action === 'update') {
            // More specific "update" descriptions
            description = `Modified ${entityLabel.toLowerCase()} '${targetName}'`;

            // Show changed fields from API
            if (log.changedFields && log.changedFields.length > 0) {
                const fields = log.changedFields.slice(0, 3).map(field => {
                    // Make field names more readable
                    return field.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim();
                }).join(', ');
                description += ` - changed ${fields}`;
                if (log.changedFields.length > 3) {
                    description += ` +${log.changedFields.length - 3} more`;
                }
            } else if (log.changeCount) {
                description += ` - ${log.changeCount} field${log.changeCount > 1 ? 's' : ''} changed`;
            }

            if (parentName && parentType) {
                description += ` in ${parentType.toLowerCase()} '${parentName}'`;
            }
        } else if (action === 'delete' || action === 'remove' || action === 'remove_attribute') {
            // More specific "delete" descriptions
            if (action === 'remove_attribute') {
                description = `Removed attribute from ${entityLabel.toLowerCase()} '${targetName}'`;
            } else if (parentName && parentType) {
                description = `Deleted ${entityLabel.toLowerCase()} '${targetName}' from ${parentType.toLowerCase()} '${parentName}'`;
            } else {
                description = `Deleted ${entityLabel.toLowerCase()} '${targetName}'`;
            }

            // Show what was deleted for media
            if (log.target.type === 'media' && log.details) {
                const fileInfo = [];
                if (log.details.extension) {
                    fileInfo.push(`.${log.details.extension}`);
                }
                if (fileInfo.length > 0) {
                    description += ` (${fileInfo.join(', ')})`;
                }
            }
        } else if (action === 'login_success') {
            description = 'Logged in successfully';
            if (log.source && log.source.ip) {
                description += ` from ${log.source.ip}`;
            }
        } else if (action === 'login_failed') {
            description = `Failed login attempt`;
            if (log.source && log.source.ip) {
                description += ` from ${log.source.ip}`;
            }
        } else if (action === 'read') {
            description = `Viewed ${entityLabel.toLowerCase()} '${targetName}'`;
        } else if (action === 'upload') {
            description = `Uploaded ${entityLabel.toLowerCase()} '${targetName}'`;
            if (parentName && parentType) {
                description += ` to ${parentType.toLowerCase()} '${parentName}'`;
            }

            // Add file details
            if (log.details) {
                const fileInfo = [];
                if (log.details.fileSize) {
                    fileInfo.push(this.humanFileSize(log.details.fileSize, true));
                }
                if (log.details.extension) {
                    fileInfo.push(`.${log.details.extension}`);
                }
                if (fileInfo.length > 0) {
                    description += ` (${fileInfo.join(', ')})`;
                }
            }
        } else if (action === 'download') {
            description = `Downloaded ${entityLabel.toLowerCase()} '${targetName}'`;
        } else {
            // Fallback: capitalize action and show entity
            const actionText = action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            description = `${actionText}: ${entityLabel.toLowerCase()} '${targetName}'`;
        }

        return description;
    }

    humanFileSize(bytes, si) {
        var thresh = si ? 1000 : 1024;
        if (Math.abs(bytes) < thresh) {
            return bytes + ' B';
        }
        var units = si ? ['KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'] : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
        var u = -1;
        do {
            bytes /= thresh;
            ++u;
        } while (Math.abs(bytes) >= thresh && u < units.length - 1);
        return bytes.toFixed(1) + ' ' + units[u];
    }

    getMethodBadgeStyle(method) {
        switch (method) {
            case 'GET':
                return 'info';
            case 'POST':
                return 'success';
            case 'PUT':
            case 'PATCH':
                return 'warning';
            case 'DELETE':
                return 'danger';
            default:
                return 'default';
        }
    }

    render() {
        const { t, logs, onViewDetails } = this.props;

        console.log('AuditLogsTable rendering with logs:', logs ? logs.length : 0, logs);

        if (!logs || logs.length === 0) {
            return (
                <div className="audit-logs-empty">
                    <p>{t('AuditLogs.noLogs')}</p>
                </div>
            );
        }

        return (
            <div className="AuditLogsTable">
                <Table striped bordered hover responsive>
                    <thead>
                        <tr>
                            <th width="140">{t('AuditLogs.timestamp')}</th>
                            <th width="200">{t('AuditLogs.user')}</th>
                            <th width="110">{t('AuditLogs.action')}</th>
                            <th>Description</th>
                            <th width="280" className="http-request-column">Request</th>
                            <th width="120"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {logs.map((log, index) => {
                            const { relative, absolute } = this.formatTimestamp(log.timestamp);
                            const actionLabel = t(`AuditLogs.actions.${log.event.action}`) || log.event.action;
                            const description = this.getDescription(log);

                            return (
                                <tr key={log.id || index} className="audit-log-row" onClick={() => onViewDetails(log)}>
                                    <td>
                                        <div className="timestamp-info">
                                            <div className="timestamp-absolute">{absolute}</div>
                                            <div className="timestamp-relative text-muted">{relative}</div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="user-info">
                                            <div className="user-name">
                                                {log.actor && log.actor.name ? log.actor.name : (log.actor && log.actor.type ? log.actor.type : 'System')}
                                            </div>
                                            {log.actor && log.actor.email && (
                                                <div className="user-email text-muted">{log.actor.email}</div>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <Label bsStyle={this.getActionBadgeStyle(log.event.action)}>
                                            {actionLabel}
                                        </Label>
                                    </td>
                                    <td>
                                        <div className="description-text">
                                            {log.event.outcome === 'failure' && (
                                                <i className="fa fa-times-circle text-danger" style={{ marginRight: '6px' }} />
                                            )}
                                            {log.event.outcome === 'success' && log.event.action !== 'read' && (
                                                <i className="fa fa-check-circle text-success" style={{ marginRight: '6px' }} />
                                            )}
                                            {description}
                                        </div>
                                        {log.event.outcome === 'failure' && log.details && log.details.error && (
                                            <div className="error-text text-danger">
                                                <small><i className="fa fa-exclamation-circle" /> {log.details.error}</small>
                                            </div>
                                        )}
                                    </td>
                                    <td className="http-request-column">
                                        {log.context && log.context.method && log.context.path && (
                                            <div className="http-request-info">
                                                <div>
                                                    <Label bsStyle={this.getMethodBadgeStyle(log.context.method)} style={{ marginRight: '6px' }}>
                                                        {log.context.method}
                                                    </Label>
                                                    <span className="http-path">{log.context.path}</span>
                                                </div>
                                                {log.context.query && Object.keys(log.context.query).length > 0 && (
                                                    <div className="http-query text-muted">
                                                        <small>?{Object.entries(log.context.query).map(([k, v]) => `${k}=${v}`).join('&')}</small>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                    <td className="text-center">
                                        <Button
                                            bsSize="xs"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onViewDetails(log);
                                            }}
                                        >
                                            {t('AuditLogs.viewDetails')}
                                        </Button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </div>
        );
    }
}

export default translate('translations')(AuditLogsTable);
